import { Injectable } from "@angular/core";
import { APP_PATHS } from "../../shared/utils/app-route-paths";
import { canonicalizeLegacyUrl } from "../../shared/utils/legacy-route-redirect";
import { resolveGroupLandingRoute } from "../../shared/utils/group-landing.util";

import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { KeycloakAuthGuard, KeycloakService } from "keycloak-angular";
import { AuthService } from "../auth/auth.service";
import { keycloakRedirectUri } from "../auth/keycloak-redirect.util";

@Injectable({
  providedIn: "root",
})
export class AuthGuard extends KeycloakAuthGuard {
  constructor(
    protected override readonly router: Router,
    protected readonly keycloak: KeycloakService,
    private readonly authService: AuthService,
  ) {
    super(router, keycloak);
  }

  public async isAccessAllowed(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    // Force login if not authenticated

    if (!this.authenticated) {
      // Store the target URL in sessionStorage
      sessionStorage.setItem("redirectAfterLogin", state.url);
      await this.keycloak.login({ redirectUri: this.getKeycloakRedirectUri() });
      return false;
    }

    const redirectUrl = this.getPostLoginRedirect(state.url);
    if (redirectUrl && redirectUrl !== state.url) {
      await this.router.navigateByUrl(redirectUrl);
      return false;
    }

    const routeChain: ActivatedRouteSnapshot[] = [];
    let currentRoute: ActivatedRouteSnapshot | null = route;
    while (currentRoute) {
      routeChain.push(currentRoute);
      currentRoute = currentRoute.firstChild;
    }

    const hasRequiredRoles = routeChain.every((snapshot) => {
      const requiredRoles = snapshot.data["roles"];
      const roleMode = snapshot.data["roleMode"] as
        | "all"
        | "any"
        | undefined;
      if (!Array.isArray(requiredRoles) || requiredRoles.length === 0) {
        return true;
      }
      return roleMode === "any"
        ? requiredRoles.some((role) => this.roles.includes(role))
        : requiredRoles.every((role) => this.roles.includes(role));
    });
    const routeDefinesRoles = routeChain.some((snapshot) =>
      Array.isArray(snapshot.data["roles"]) && snapshot.data["roles"].length > 0,
    );

    const hasRequiredGroups = routeChain.every((snapshot) => {
      const requiredGroups = snapshot.data["groups"];
      if (!Array.isArray(requiredGroups) || requiredGroups.length === 0) {
        return true;
      }
      const groupMode = snapshot.data["groupMode"] as
        | "all"
        | "any"
        | undefined;
      if (groupMode === "all") {
        return requiredGroups.every((group) =>
          this.authService.hasAnyGroup([group]),
        );
      }
      return this.authService.hasAnyGroup(requiredGroups);
    });

    const hasAccess = routeDefinesRoles ? hasRequiredRoles : hasRequiredGroups;

    // If access is allowed, continue
    if (hasAccess) return true;

    // If user has no access, redirect safely
    // Try redirect to previously saved URL if any
    const storedRedirectUrl = this.getPostLoginRedirect(state.url);
    if (storedRedirectUrl) {
      this.router.navigateByUrl(storedRedirectUrl);
      return false;
    }

    // Otherwise, fallback to first available group landing (messaging first, admin last)
    this.router.navigateByUrl(this.resolveGroupLandingRoute());
    return false;
  }

  private resolveGroupLandingRoute(): string {
    return resolveGroupLandingRoute(
      (group) => this.authService.hasAnyGroup([group]),
      APP_PATHS.notFound,
      (role) => this.roles.includes(role),
    );
  }

  private getPostLoginRedirect(currentUrl: string): string | null {
    const redirectUrl = sessionStorage.getItem("redirectAfterLogin");
    if (!this.isSafeRedirectUrl(redirectUrl)) {
      sessionStorage.removeItem("redirectAfterLogin");
      return null;
    }

    const canonicalUrl = canonicalizeLegacyUrl(redirectUrl) ?? redirectUrl;

    if (canonicalUrl === currentUrl) {
      sessionStorage.removeItem("redirectAfterLogin");
      return null;
    }

    sessionStorage.removeItem("redirectAfterLogin");
    return canonicalUrl;
  }

  private isSafeRedirectUrl(value: string | null): value is string {
    return !!value && value.startsWith("/") && !value.startsWith("//");
  }

  private getKeycloakRedirectUri(): string {
    return keycloakRedirectUri(window.location.origin);
  }
}
